<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import { getEntities, Entity, deleteEntity } from '@/app/core/services/entities'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')

const acceptQuestion = ref()
const descriptionModal = ref()
const loading = ref(false)

/* Datos */
const search = ref()
const tableEntity = ref(null)
const entityForDelete = ref()

/* Datos componente table */
const columns = ref([
  { name: 'code', title: t('entities.list.code'), table: 'code' },
  { name: 'name', title: t('entities.list.name'), table: 'name' },
  {
    name: 'entity_type',
    title: t('entities.list.entityType'),
    table: 'entity_type'
  },
  {
    name: 'updated_at',
    title: t('entities.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<Entity[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createEntity = () => {
  void router.push({ name: 'CreateEntity' })
}
const updateEntity = (id: any) => {
  void router.push({ name: 'UpdateEntity', params: { id } })
}
const getEntity = (id: any) => {
  void router.push({ name: 'GetEntity', params: { id } })
}

/* Eliminar entidad */
const removeEntity = (values: any) => {
  showModal.value = true
  titleModal.value = t('entities.list.actionStatus', {
    status: t('table.action.delete'),
    entity: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  entityForDelete.value = values.id
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteEntity(entityForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('entities.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void entities()
    })
    .catch(async (error: any) => {
      notificationError(error, 'entities', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar entidades */
const entities = async () => {
  loading.value = true
  try {
    const data: any = await getEntities(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'entities', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm()" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('entities.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('entities.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="entities" />
          <DBtn @click.prevent="entities" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createEntity()"
          v-if="store.hasPermission('entity:create')">
          {{ t('form.button.create', { form: t('entities.list.title', 0) }) }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableEntity"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), entities()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), entities()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('entity:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="getEntity(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('entity:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateEntity(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('entity:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="removeEntity(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
